import { type PointerEvent as ReactPointerEvent, useSyncExternalStore } from 'react';
import type { ConversationDockPreview } from '@/lib/conversations/conversation-layout';
import { panelSplitPreviewAt } from '@/lib/desktop-shell/panel-split';

let preview: ConversationDockPreview | null = null;
const listeners = new Set<() => void>();

/** Shows where a tab dragged over the panel would go (`null`: nowhere). */
export function showPanelSplitPreview(next: ConversationDockPreview | null): void {
  if (next?.targetId === preview?.targetId && next?.side === preview?.side) return;
  preview = next;
  for (const listener of listeners) listener();
}

/** Where a tab dragged over the panel would go, which the panel outlines. */
export function usePanelSplitPreview(): ConversationDockPreview | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => preview,
    () => null,
  );
}

/**
 * Drags something listed outside the tab strip (a page of the browser, an item of an app, in the
 * sidebar) over the panel's views: dropped beside the tab under the pointer, `onDrop` shows it
 * there. Without moving, it stays a click.
 */
export function dragIntoPanelSplit(
  event: ReactPointerEvent<HTMLElement>,
  onDrop: (preview: ConversationDockPreview) => void,
): void {
  if (event.button !== 0) return;
  const element = event.currentTarget;
  const { pointerId } = event;
  const origin = { x: event.clientX, y: event.clientY };
  let dragging = false;
  let target: ConversationDockPreview | null = null;

  function move(pointerEvent: PointerEvent): void {
    if (pointerEvent.pointerId !== pointerId) return;
    if (
      !dragging &&
      Math.hypot(pointerEvent.clientX - origin.x, pointerEvent.clientY - origin.y) < 4
    )
      return;
    if (!dragging) {
      dragging = true;
      // Over a page (a webview, an app's frame) the document only gets the pointer while captured.
      try {
        element.setPointerCapture(pointerId);
      } catch {
        // Already released: the drop ends the drag anyway.
      }
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
    }
    pointerEvent.preventDefault();
    target = panelSplitPreviewAt(pointerEvent.clientX, pointerEvent.clientY);
    showPanelSplitPreview(target);
  }

  function finish(pointerEvent: PointerEvent): void {
    if (pointerEvent.pointerId !== pointerId) return;
    document.removeEventListener('pointermove', move);
    document.removeEventListener('pointerup', finish);
    document.removeEventListener('pointercancel', finish);
    document.body.style.removeProperty('user-select');
    document.body.style.removeProperty('cursor');
    if (!dragging) return;
    showPanelSplitPreview(null);
    // The click ending the drag would select what was dragged.
    const swallow = (click: MouseEvent) => {
      click.stopPropagation();
      click.preventDefault();
    };
    window.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
    if (target !== null && pointerEvent.type === 'pointerup') onDrop(target);
  }

  document.addEventListener('pointermove', move, { passive: false });
  document.addEventListener('pointerup', finish);
  document.addEventListener('pointercancel', finish);
}
